import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Loader2, Pencil, RefreshCw, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";
import type { InvoiceFileOut } from "@/lib/api/jobs";
import EditRecordModal from "@/features/records/EditRecordModal";
import DiffReviewPanel from "@/features/records/DiffReviewPanel";
import { FIELD_META_MAP } from "@/features/records/fieldLabels";
import { useReprocess } from "@/features/records/useReprocess";
import { useClearReviewComplete, useSetReviewComplete } from "@/features/records/useReviewComplete";
import { useCorrectionStatusWatcher } from "@/features/records/corrections/useCorrections";
import AutomationBadge from "@/features/records/AutomationBadge";
import { isAutoFixPending } from "@/features/records/automationStatusUtil";
import ReviewStatusBadge from "@/features/records/ReviewStatus";
import {
  deriveReviewState,
  REVIEW_STATE_I18N_KEYS,
  type ReviewDisplayState,
} from "@/features/records/reviewStatusUtil";

import StatusBadge from "./StatusBadge";
import { formatBytes } from "../upload/constants";

interface Props {
  jobId: string;
  files: InvoiceFileOut[];
  /** 관리자 감독 조회 — true 면 편집/검수/재처리 등 쓰기 액션을 잠근다. */
  readOnly?: boolean;
}

type Filter = "all" | "ok" | "rejected" | "error" | "pending";

/** ``Filter`` 값 순서를 명시적으로 정의 — ``Object.keys`` 순회는 보장되나
 * 정의 시점의 순서가 그대로 노출되도록 별도 배열로 분리. */
const FILTER_KEYS: Filter[] = ["all", "ok", "rejected", "error", "pending"];

/**
 * 검수 상태 필터 — ``deriveReviewState`` 파생값과 1:1(+``all``). 배지와 동일 기준.
 * ``autoFixPending`` 은 검수 상태와 별개로 자동 검수가 고친 값을 아직 확인하지 않은 건.
 */
type ReviewFilter = "all" | ReviewDisplayState | "autoFixPending";

const REVIEW_FILTER_KEYS: ReviewFilter[] = [
  "all",
  "none",
  "running",
  "needsReview",
  "autoFixPending",
  "passed",
  "confirmed",
];

/** csv_data 에서 안전하게 문자열 추출. */
function getStr(csv: Record<string, unknown> | null | undefined, key: string): string {
  const v = csv?.[key];
  return v == null ? "" : String(v);
}

/** 파일 1건의 표시용 분류. */
function classify(file: InvoiceFileOut): Exclude<Filter, "all"> {
  if (file.status === "failed") return "error";
  if (file.record?.is_rejected) return "rejected";
  if (file.record) return "ok";
  return "pending";
}

export default function RecordTable({ jobId, files, readOnly = false }: Props) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("all");
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>("all");
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [reviewingFileId, setReviewingFileId] = useState<string | null>(null);
  // M15 — 수정제안 처리 중인 파일들 (fileId → 진행 중인 correction requestId).
  const [correcting, setCorrecting] = useState<Record<string, string>>({});

  const clearCorrecting = useCallback((fileId: string) => {
    setCorrecting((prev) => {
      if (!(fileId in prev)) return prev;
      return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== fileId));
    });
  }, []);

  const editingFile = useMemo(
    () => files.find((f) => f.id === editingFileId) ?? null,
    [files, editingFileId],
  );
  const reviewingFile = useMemo(
    () => files.find((f) => f.id === reviewingFileId) ?? null,
    [files, reviewingFileId],
  );

  // pending 이 해제된 파일을 검토 상태에서 빠르게 닫기 (apply/discard 후).
  useEffect(() => {
    if (reviewingFileId && reviewingFile && reviewingFile.record?.pending_extraction == null) {
      setReviewingFileId(null);
    }
  }, [reviewingFileId, reviewingFile]);

  const reprocessMutation = useReprocess();

  const counts = useMemo(() => {
    const c: Record<Exclude<Filter, "all">, number> = {
      ok: 0,
      rejected: 0,
      error: 0,
      pending: 0,
    };
    for (const f of files) c[classify(f)] += 1;
    return c;
  }, [files]);

  const reviewCounts = useMemo(() => {
    const c: Record<ReviewDisplayState, number> = {
      none: 0,
      running: 0,
      passed: 0,
      needsReview: 0,
      confirmed: 0,
    };
    for (const f of files) c[deriveReviewState(f.record)] += 1;
    return c;
  }, [files]);

  const autoFixPendingCount = useMemo(
    () => files.filter((f) => isAutoFixPending(f.record)).length,
    [files],
  );

  const visible = useMemo(() => {
    return files.filter((f) => {
      if (filter !== "all" && classify(f) !== filter) return false;
      if (reviewFilter === "autoFixPending") return isAutoFixPending(f.record);
      if (reviewFilter !== "all" && deriveReviewState(f.record) !== reviewFilter) return false;
      return true;
    });
  }, [files, filter, reviewFilter]);

  // ────────────── M13 — 편집 모달 paging ──────────────
  //
  // 정책 (사용자 합의 — Q3=filtered):
  //   - paging 대상은 현재 ``visible`` (filter 적용 후의 파일 리스트).
  //   - 사용자가 '정상' 필터에서 편집을 시작하면 정상 파일만 paging.
  //   - 필터를 변경하면 paging 범위가 즉시 바뀐다.
  //
  // 사용자가 편집 중에 워커가 추가 처리를 끝내 ``files`` 자체가 갱신
  // 되면 (예: pending → ok), 그 결과로 만들어진 새 ``visible`` 의 인덱스
  // 가 자연스럽게 paging 범위가 된다. 만약 현재 편집 중인 파일이 필터
  // 결과에서 빠지면 ``currentIdx === -1`` 이 되어 prev/next 둘 다 비활
  // 성화 — 사용자는 닫기/저장 후 닫기 만 가능.
  const visibleIds = useMemo(() => visible.map((f) => f.id), [visible]);
  const currentIdx = editingFileId ? visibleIds.indexOf(editingFileId) : -1;
  const canPrev = currentIdx > 0;
  const canNext = currentIdx >= 0 && currentIdx < visibleIds.length - 1;
  // ``noUncheckedIndexedAccess`` 대응: 인덱스 접근이 ``string | undefined``
  // 로 추론되므로 명시적 null 체크 후 setState.
  const goPrev = () => {
    if (!canPrev) return;
    const next = visibleIds[currentIdx - 1];
    if (next) setEditingFileId(next);
  };
  const goNext = () => {
    if (!canNext) return;
    const next = visibleIds[currentIdx + 1];
    if (next) setEditingFileId(next);
  };

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      {/* 필터 바 */}
      <div className="flex items-center gap-1 border-b border-slate-100 bg-slate-50 px-3 py-2">
        {FILTER_KEYS.map((key) => {
          const total = key === "all" ? files.length : counts[key as Exclude<Filter, "all">];
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              disabled={total === 0 && key !== "all"}
              className={cn(
                "rounded px-2.5 py-1 text-xs transition",
                active ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-white",
                total === 0 && key !== "all" && "cursor-not-allowed opacity-40",
              )}
            >
              {t(`jobs.detail.records.filter.${key}`)}
              <span className="ml-1 opacity-70">{total}</span>
            </button>
          );
        })}

        {/* 검수 상태 필터 (드롭다운) — 처리상태 필터와 AND 조합. */}
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
          <span>{t("jobs.detail.records.reviewFilter.label")}</span>
          <select
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value as ReviewFilter)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700"
          >
            {REVIEW_FILTER_KEYS.map((key) => {
              const label =
                key === "all"
                  ? t("jobs.detail.records.reviewFilter.all")
                  : key === "autoFixPending"
                    ? t("records.automation.autoFix.pending")
                    : t(REVIEW_STATE_I18N_KEYS[key]);
              const total =
                key === "all"
                  ? files.length
                  : key === "autoFixPending"
                    ? autoFixPendingCount
                    : reviewCounts[key];
              return (
                <option key={key} value={key}>
                  {label} ({total})
                </option>
              );
            })}
          </select>
        </label>
      </div>

      {/* 테이블 */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <Th>{t("jobs.detail.records.th.no")}</Th>
              <Th className="text-left">{t(FIELD_META_MAP.document_id.labelI18nKey)}</Th>
              <Th>{t(FIELD_META_MAP.local_agent_code.labelI18nKey)}</Th>
              <Th className="text-left">{t(FIELD_META_MAP.agent_name.labelI18nKey)}</Th>
              <Th>{t(FIELD_META_MAP.total_amount.labelI18nKey)}</Th>
              <Th>{t(FIELD_META_MAP.withholding_tax.labelI18nKey)}</Th>
              <Th>{t(FIELD_META_MAP.ref_number.labelI18nKey)}</Th>
              <Th>{t("jobs.detail.records.th.status")}</Th>
              <Th>{t("jobs.detail.records.th.review")}</Th>
              <Th>{t("jobs.detail.records.th.actions")}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-400">
                  {t("jobs.detail.records.empty")}
                </td>
              </tr>
            ) : (
              visible.map((file, idx) => {
                const kind = classify(file);
                const csv = file.record?.csv_data;
                return (
                  <tr
                    key={file.id}
                    className={cn(
                      "transition hover:bg-slate-50",
                      kind === "rejected" && "bg-amber-50/30",
                      kind === "error" && "bg-red-50/30",
                    )}
                  >
                    <Td className="text-right text-xs text-slate-500">{idx + 1}</Td>
                    <Td className="text-left">
                      <div
                        className="max-w-[280px] truncate text-slate-800"
                        title={file.original_name}
                      >
                        {file.original_name}
                      </div>
                      <div className="text-[10px] text-slate-400">{formatBytes(file.size)}</div>
                    </Td>
                    <Td>
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">
                        {getStr(csv, "local_agent_code") || "—"}
                      </code>
                    </Td>
                    <Td className="text-left">
                      <span
                        className="line-clamp-1 max-w-[200px] text-slate-700"
                        title={file.record?.delivered_agent_name ?? getStr(csv, "agent_name")}
                      >
                        {file.record?.delivered_agent_name ?? (getStr(csv, "agent_name") || "—")}
                      </span>
                    </Td>
                    <Td className="text-right tabular-nums">
                      {getStr(csv, "total_amount") || "—"}
                    </Td>
                    <Td className="text-right tabular-nums text-slate-500">
                      {getStr(csv, "withholding_tax") || "—"}
                    </Td>
                    <Td>
                      <span className="text-xs text-slate-600">
                        {getStr(csv, "ref_number") || "—"}
                      </span>
                    </Td>
                    <Td>
                      <div className="space-y-0.5">
                        <StatusBadge status={file.status} />
                        {file.record?.is_rejected && file.record.rejection_reason && (
                          <p
                            className="line-clamp-2 max-w-[200px] text-[10px] text-amber-700"
                            title={file.record.rejection_reason}
                          >
                            {file.record.rejection_reason}
                          </p>
                        )}
                        {file.status === "failed" && file.error_message && (
                          <p
                            className="line-clamp-2 max-w-[200px] text-[10px] text-red-700"
                            title={file.error_message}
                          >
                            {file.error_message}
                          </p>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <ReviewCell jobId={jobId} file={file} readOnly={readOnly} />
                    </Td>
                    <Td>
                      <ActionCell
                        file={file}
                        readOnly={readOnly}
                        onEdit={() => setEditingFileId(file.id)}
                        onReview={() => setReviewingFileId(file.id)}
                        onReprocess={() =>
                          reprocessMutation.mutate({
                            jobId,
                            fileId: file.id,
                          })
                        }
                        reprocessingId={
                          reprocessMutation.isPending
                            ? (reprocessMutation.variables?.fileId ?? null)
                            : null
                        }
                        correcting={file.id in correcting}
                      />
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editingFile && (
        <EditRecordModal
          jobId={jobId}
          file={editingFile}
          readOnly={readOnly}
          onClose={() => setEditingFileId(null)}
          position={
            currentIdx >= 0 ? { current: currentIdx + 1, total: visibleIds.length } : undefined
          }
          onPrev={goPrev}
          onNext={goNext}
          canPrev={canPrev}
          canNext={canNext}
          onCorrectionSubmitted={(fileId, requestId) =>
            setCorrecting((prev) => ({ ...prev, [fileId]: requestId }))
          }
        />
      )}
      {reviewingFile && (
        <DiffReviewPanel
          jobId={jobId}
          file={reviewingFile}
          onClose={() => setReviewingFileId(null)}
          onApplied={() => {
            // 검토 반영 후 목록이 아닌 케이스 편집 화면으로 이어지도록 전환.
            const fid = reviewingFileId;
            setReviewingFileId(null);
            if (fid) setEditingFileId(fid);
          }}
        />
      )}

      {/* M15 — 진행 중인 수정제안 폴링 watcher (보이지 않음) */}
      {Object.entries(correcting).map(([fileId, requestId]) => (
        <CorrectionWatcher
          key={`${fileId}:${requestId}`}
          jobId={jobId}
          fileId={fileId}
          requestId={requestId}
          onDone={() => clearCorrecting(fileId)}
        />
      ))}
    </section>
  );
}

/** 진행 중 correction 1건을 폴링하다 종료되면 onDone 호출 (UI 없음). */
function CorrectionWatcher({
  jobId,
  fileId,
  requestId,
  onDone,
}: {
  jobId: string;
  fileId: string;
  requestId: string;
  onDone: () => void;
}) {
  useCorrectionStatusWatcher(jobId, fileId, requestId, onDone);
  return null;
}

/** M21 #7 — 검수 완료 체크박스 + 상태 배지. */
function ReviewCell({
  jobId,
  file,
  readOnly = false,
}: {
  jobId: string;
  file: InvoiceFileOut;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const setMutation = useSetReviewComplete();
  const clearMutation = useClearReviewComplete();
  const record = file.record;

  if (!record) {
    return <span className="text-[10px] text-slate-400">—</span>;
  }

  const checked = record.review_status !== "none";
  const busy = setMutation.isPending || clearMutation.isPending || readOnly;

  const toggle = () => {
    if (busy || readOnly) return;
    if (checked) {
      clearMutation.mutate({ jobId, fileId: file.id });
    } else {
      setMutation.mutate({ jobId, fileId: file.id });
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <label className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-slate-600">
        <input
          type="checkbox"
          checked={checked}
          disabled={busy}
          onChange={toggle}
          className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
          aria-label={t("records.review.checkbox")}
        />
      </label>
      <ReviewStatusBadge record={record} />
      <AutomationBadge automation={record.automation} />
    </div>
  );
}

function ActionCell({
  file,
  onEdit,
  onReview,
  onReprocess,
  reprocessingId,
  correcting = false,
  readOnly = false,
}: {
  file: InvoiceFileOut;
  onEdit: () => void;
  onReview: () => void;
  onReprocess: () => void;
  reprocessingId: string | null;
  correcting?: boolean;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const hasRecord = !!file.record;
  const pending = file.record?.pending_extraction != null;
  const isReprocessing = reprocessingId === file.id;

  if (!hasRecord) {
    return <span className="text-[10px] text-slate-400">—</span>;
  }

  // 관리자 감독(읽기전용): '보기' 버튼만 노출(모달은 읽기전용). 재처리/검토채택 잠금.
  if (readOnly) {
    return (
      <div className="flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50"
        >
          <Eye className="h-3 w-3" />
          {t("jobs.detail.records.action.view")}
        </button>
      </div>
    );
  }

  // M15 — 수정제안 처리 중: 진행 링만 노출.
  if (correcting) {
    return (
      <div className="flex flex-col items-center gap-1">
        <span className="inline-flex items-center gap-1 rounded-md border border-violet-200 bg-violet-50 px-2 py-1 text-[11px] text-violet-700">
          <Loader2 className="h-3 w-3 animate-spin" />
          <Sparkles className="h-3 w-3" />
          {t("jobs.detail.records.action.correcting")}
        </span>
      </div>
    );
  }

  if (pending) {
    // 검토 대기 — 검토 버튼만 노출 (편집/재처리는 잠금)
    return (
      <div className="flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={onReview}
          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 transition hover:bg-amber-100"
        >
          <Eye className="h-3 w-3" />
          {t("jobs.detail.records.action.review")}
        </button>
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
          {t("jobs.detail.records.reviewPending")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50"
      >
        <Pencil className="h-3 w-3" />
        {t("jobs.detail.records.action.edit")}
      </button>
      <button
        type="button"
        onClick={onReprocess}
        disabled={isReprocessing}
        title={t("jobs.detail.records.reprocessTooltip")}
        className={cn(
          "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] transition",
          isReprocessing
            ? "cursor-wait border-slate-200 text-slate-400"
            : "border-slate-200 text-slate-600 hover:bg-slate-50",
        )}
      >
        {isReprocessing ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <RefreshCw className="h-3 w-3" />
        )}
        {t("jobs.detail.records.action.reprocess")}
      </button>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th scope="col" className={cn("px-3 py-2 text-center font-medium", className)}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 text-center align-top", className)}>{children}</td>;
}
